import { Observable, BehaviorSubject, Subject, fromEvent } from 'rxjs';
import { map, filter, takeUntil } from 'rxjs/operators';
import { 
  WorkerMessage, 
  UploadProgress, 
  UploadResult, 
  UploadConfig,
  WorkerMessageType
} from '../types/upload.types';

/**
 * Client service for communicating with the async upload worker
 * 
 * Provides a reactive interface for managing file uploads with
 * progress tracking, job control, and error handling.
 */
export class AsyncUploadClient {
  private worker?: Worker;
  private workerSubject = new BehaviorSubject<Worker | null>(null);
  private messageSubject = new Subject<WorkerMessage>();
  private destroySubject = new Subject<void>();
  private isInitialized = false;

  constructor(private config?: UploadConfig) {}

  /**
   * Initialize the worker
   */
  async initialize(workerScriptPath?: string): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      // Default to the built worker path
      const scriptPath = workerScriptPath || '/assets/async-upload.worker.min.js';
      
      this.worker = new Worker(scriptPath);
      this.workerSubject.next(this.worker);
      
      // Set up message handling
      fromEvent<MessageEvent>(this.worker, 'message')
        .pipe(takeUntil(this.destroySubject))
        .subscribe(event => {
          this.messageSubject.next(event.data);
        });

      // Handle worker errors
      fromEvent<ErrorEvent>(this.worker, 'error')
        .pipe(takeUntil(this.destroySubject))
        .subscribe(error => {
          console.error('Worker error:', error);
          this.messageSubject.next({
            type: 'JOB_ERROR',
            jobId: 'worker',
            timestamp: Date.now(),
            error: `Worker error: ${error.message}`,
          } as WorkerMessage);
        });

      this.isInitialized = true;
      console.log('AsyncUploadClient initialized successfully');
    } catch (error) {
      console.error('Failed to initialize AsyncUploadClient:', error);
      throw error;
    }
  }

  /**
   * Start an upload job
   */
  startUpload(
    jobId: string,
    files: File[],
    presignedUrls: string[],
    additionalData?: Record<string, unknown>
  ): Observable<WorkerMessage> {
    if (!this.worker) {
      throw new Error('Worker not initialized. Call initialize() first.');
    }

    if (files.length !== presignedUrls.length) {
      throw new Error('Files and presigned URLs arrays must have the same length');
    }

    // Send upload request to worker
    this.worker.postMessage({
      type: 'UPLOAD_REQUEST',
      jobId,
      timestamp: Date.now(),
      data: {
        files,
        presignedUrls,
        config: this.config,
        additionalData,
      },
    });

    // Return observable filtered for this job
    return this.getJobMessages(jobId);
  }

  /**
   * Pause an upload job
   */
  pauseJob(jobId: string): void {
    this.sendJobControlMessage(WorkerMessageType.PAUSE_JOB, jobId);
  }

  /**
   * Resume an upload job
   */
  resumeJob(jobId: string): void {
    this.sendJobControlMessage(WorkerMessageType.RESUME_JOB, jobId);
  }

  /**
   * Cancel an upload job
   */
  cancelJob(jobId: string): void {
    this.sendJobControlMessage(WorkerMessageType.CANCEL_JOB, jobId);
  }

  /**
   * Get progress updates for a specific job
   */
  getJobProgress(jobId: string): Observable<UploadProgress> {
    return this.getJobMessageObservable(jobId, WorkerMessageType.UPLOAD_PROGRESS, message => (message as WorkerMessage & { progress: UploadProgress }).progress);
  }

  /**
   * Get completion notification for a specific job
   */
  getJobCompletion(jobId: string): Observable<UploadResult[]> {
    return this.getJobMessageObservable(jobId, WorkerMessageType.UPLOAD_COMPLETE, message => (message as WorkerMessage & { results: UploadResult[] }).results);
  }

  /**
   * Get error notifications for a specific job
   */
  getJobErrors(jobId: string): Observable<string> {
    return this.messageSubject.pipe(
      filter(message => 
        message.jobId === jobId && 
        (message.type === 'UPLOAD_FAILED' || message.type === 'JOB_ERROR')
      ),
      map(message => (message as WorkerMessage & { error: string }).error),
      takeUntil(this.destroySubject)
    );
  }

  /**
   * Get status updates for a specific job
   */
  getJobStatus(jobId: string): Observable<{ status: string; message?: string }> {
    return this.messageSubject.pipe(
      filter(message => 
        message.jobId === jobId && 
        ['JOB_STARTED', 'JOB_PAUSED', 'JOB_RESUMED', 'JOB_CANCELED'].includes(message.type)
      ),
      map(message => ({
        status: message.type.replace('JOB_', ''),
        message: (message as WorkerMessage & { message?: string }).message,
      })),
      takeUntil(this.destroySubject)
    );
  }

  /**
   * Get all messages for a specific job
   */
  getJobMessages(jobId: string): Observable<WorkerMessage> {
    return this.messageSubject.pipe(
      filter(message => message.jobId === jobId),
      takeUntil(this.destroySubject)
    );
  }

  /**
   * Get all worker messages
   */
  getAllMessages(): Observable<WorkerMessage> {
    return this.messageSubject.pipe(
      takeUntil(this.destroySubject)
    );
  }

  /**
   * Check if worker is initialized and ready
   */
  isReady(): boolean {
    return this.isInitialized && this.worker !== undefined;
  }

  /**
   * Get current worker instance
   */
  getWorker(): Worker | null {
    return this.worker || null;
  }

  /**
   * Terminate the worker and clean up resources
   */
  destroy(): void {
    if (this.worker) {
      this.worker.terminate();
      this.worker = undefined;
    }
    
    this.workerSubject.next(null);
    this.destroySubject.next();
    this.destroySubject.complete();
    this.messageSubject.complete();
    this.workerSubject.complete();
    
    this.isInitialized = false;
    console.log('AsyncUploadClient destroyed');
  }

  /**
   * Send a job control message to the worker
   */
  private sendJobControlMessage(type: WorkerMessageType, jobId: string): void {
    if (!this.worker) {
      throw new Error('Worker not initialized. Call initialize() first.');
    }

    this.worker.postMessage({
      type,
      jobId,
      timestamp: Date.now(),
    });
  }

  /**
   * Create a filtered observable for a specific job and message type
   */
  private getJobMessageObservable<T>(
    jobId: string, 
    messageType: WorkerMessageType,
    extractData?: (message: WorkerMessage) => T
  ): Observable<T> {
    return this.messageSubject.pipe(
      filter(message => message.jobId === jobId && message.type === messageType),
      map(message => extractData ? extractData(message) : message as T),
      takeUntil(this.destroySubject)
    );
  }
}

/**
 * Factory function to create and initialize an AsyncUploadClient
 */
export async function createAsyncUploadClient(
  config?: UploadConfig,
  workerScriptPath?: string
): Promise<AsyncUploadClient> {
  const client = new AsyncUploadClient(config);
  await client.initialize(workerScriptPath);
  return client;
}
